import {
  DrugReferenceFgNdcToBulkNdc,
  DrugReferenceFgNdcToBulkNdcPayload
} from 'Interfaces/ReferenceInterfaces/DrugReferenceFgNdcToBulkNdcInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { drugReferenceFgNdcToBulkNdcServices } from 'services/ReferenceScreenServices/drugReferenceFgNdcToBulkNdcServices';

export class DrugReferenceFgNdcToBulkNdcStore {
  drugReferenceFgNdcToBulkNdcs: DrugReferenceFgNdcToBulkNdc[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  searchQuery: string = '';
  selectedDrugReferenceFgNdcToBulkNdc: DrugReferenceFgNdcToBulkNdc | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchDrugReferenceFgNdcToBulkNdcs = async (page = 1, pageSize = 10000, search?: string) => {
    this.isLoading = true;
    try {
      const response = await drugReferenceFgNdcToBulkNdcServices.getAllDrugReferenceFgNdcToBulkNdcs(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        this.drugReferenceFgNdcToBulkNdcs = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
        if (search !== undefined) {
          this.searchQuery = search;
        }
      });
    } catch (error) {
      toast.error('Error fetching drug reference FG NDC to bulk NDC');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollDrugReferenceFgNdcToBulkNdcs = async (page = 1, pageSize = 10000, search?: string) => {
    this.isPolling = true;
    try {
      const response = await drugReferenceFgNdcToBulkNdcServices.getAllDrugReferenceFgNdcToBulkNdcs(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.drugReferenceFgNdcToBulkNdcs) !== JSON.stringify(response.data)) {
          this.drugReferenceFgNdcToBulkNdcs = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
          if (search !== undefined) {
            this.searchQuery = search;
          }
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if (this.environment === 'development') {
        console.error('Error polling drug reference FG NDC to bulk NDC:', error);
      }
      toast.error('Error polling drug reference FG NDC to bulk NDC!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 10000, search?: string) => {
    await this.fetchDrugReferenceFgNdcToBulkNdcs(page, pageSize, search);
  };

  createDrugReferenceFgNdcToBulkNdc = async (
    drugReferenceData: DrugReferenceFgNdcToBulkNdcPayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await drugReferenceFgNdcToBulkNdcServices.createDrugReferenceFgNdcToBulkNdc(
          drugReferenceData
        );
      if (response.status) {
        toast.success('Drug reference FG NDC to bulk NDC created successfully');
        await this.fetchDrugReferenceFgNdcToBulkNdcs(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create drug reference FG NDC to bulk NDC');
        return false;
      }
    } catch (error) {
      toast.error('Error creating drug reference FG NDC to bulk NDC');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateDrugReferenceFgNdcToBulkNdc = async (
    id: number,
    drugReferenceData: DrugReferenceFgNdcToBulkNdcPayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await drugReferenceFgNdcToBulkNdcServices.updateDrugReferenceFgNdcToBulkNdc(
        id,
        drugReferenceData
      );
      if (response.status) {
        toast.success('Drug reference FG NDC to bulk NDC updated successfully');
        await this.fetchDrugReferenceFgNdcToBulkNdcs(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update drug reference FG NDC to bulk NDC');
        return false;
      }
    } catch (error) {
      toast.error('Error updating drug reference FG NDC to bulk NDC');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteDrugReferenceFgNdcToBulkNdc = async (id: number): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await drugReferenceFgNdcToBulkNdcServices.deleteDrugReferenceFgNdcToBulkNdc(id);
      if (response.status) {
        toast.success('Drug reference FG NDC to bulk NDC deleted successfully');

        const isLastItemOnPage = this.drugReferenceFgNdcToBulkNdcs.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchDrugReferenceFgNdcToBulkNdcs(
            this.pagination.currentPage - 1,
            10,
            this.searchQuery || undefined
          );
        } else {
          await this.fetchDrugReferenceFgNdcToBulkNdcs(
            this.pagination.currentPage,
            10,
            this.searchQuery || undefined
          );
        }
        return true;
      } else {
        toast.error(response.message || 'Failed to delete drug reference FG NDC to bulk NDC');
        return false;
      }
    } catch (error) {
      toast.error('Error deleting drug reference FG NDC to bulk NDC');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedDrugReferenceFgNdcToBulkNdc = (drugReference: DrugReferenceFgNdcToBulkNdc | null) => {
    this.selectedDrugReferenceFgNdcToBulkNdc = drugReference;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };
}

export const drugReferenceFgNdcToBulkNdcStore = new DrugReferenceFgNdcToBulkNdcStore();
